import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Plus, Trash2, ArrowRightLeft, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { formatFCFA } from "../lib/fcfa";

const empty = {
  player_id: "", player_name: "", type: "arrivee",
  from_club: "", to_club: "",
  transfer_date: new Date().toISOString().slice(0, 10),
  fee: "", contract_duration: "", notes: "",
};

export default function Transfers() {
  const [list, setList] = useState([]);
  const [players, setPlayers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => {
    const q = filter === "all" ? {} : { params: { type: filter } };
    api.get("/transfers", filter === "all" ? {} : q).then(({ data }) => setList(data));
  };
  useEffect(() => { load(); api.get("/players").then(({ data }) => setPlayers(data)); }, [filter]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/transfers", {
        ...form,
        fee: parseInt(form.fee || 0, 10),
        player_id: form.player_id || null,
      });
      toast.success("Transfert enregistré");
      setOpen(false); setForm(empty); load();
    } catch (err) { toast.error("Erreur"); }
  };

  const onDel = async (id) => {
    if (!confirm("Supprimer ce transfert ?")) return;
    await api.delete(`/transfers/${id}`);
    load();
  };

  const totalArrivees = list.filter(t => t.type === "arrivee").reduce((s, t) => s + (t.fee || 0), 0);
  const totalDeparts = list.filter(t => t.type === "depart").reduce((s, t) => s + (t.fee || 0), 0);

  return (
    <div>
      <PageHeader
        overline="Mercato"
        title="Transferts"
        description="Arrivées et départs avec historique financier."
        actions={
          <Button onClick={() => setOpen(true)} data-testid="create-transfer-btn"
            className="btn-club">
            <Plus className="w-4 h-4 mr-2" /> Nouveau transfert
          </Button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <KPI icon={ArrowDownLeft} label="Total arrivées (coûts)" value={formatFCFA(totalArrivees)} color="#C62828" testid="kpi-arrivees" />
        <KPI icon={ArrowUpRight} label="Total départs (recettes)" value={formatFCFA(totalDeparts)} color="#2E7D32" testid="kpi-departs" />
        <KPI icon={ArrowRightLeft} label="Bilan mercato" value={formatFCFA(totalDeparts - totalArrivees)} color={totalDeparts - totalArrivees >= 0 ? "#2E7D32" : "#C62828"} testid="kpi-bilan-mercato" />
      </div>

      <div className="flex gap-2 mb-4">
        {[["all", "Tous"], ["arrivee", "Arrivées"], ["depart", "Départs"]].map(([k, l]) => (
          <Button key={k} variant={filter === k ? "default" : "outline"} size="sm" onClick={() => setFilter(k)}
            className={filter === k ? "btn-club" : ""}
            data-testid={`transfer-filter-${k}`}>
            {l}
          </Button>
        ))}
      </div>

      <div className="bg-white border border-[#E0E0E0] rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="transfers-table">
            <thead className="bg-[#F4F5F7] text-[#616161] tracking-overline text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Joueur</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Club</th>
                <th className="text-left px-4 py-3">Durée</th>
                <th className="text-right px-4 py-3">Montant</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E0E0E0]">
              {list.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[#616161]">Aucun transfert.</td></tr>
              ) : list.map((t) => (
                <tr key={t.id} className="hover:bg-[#F4F5F7]/50" data-testid={`transfer-row-${t.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{t.transfer_date}</td>
                  <td className="px-4 py-3 font-medium">{t.player_name}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={t.type === "arrivee" ? "bg-[#1976D2]/10 text-[#1976D2] border-[#1976D2]/30" : "bg-[#F57F17]/10 text-[#F57F17] border-[#F57F17]/30"}>
                      {t.type === "arrivee" ? "Arrivée" : "Départ"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-[#616161]">{t.type === "arrivee" ? (t.from_club || "—") : (t.to_club || "—")}</td>
                  <td className="px-4 py-3">{t.contract_duration || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatFCFA(t.fee || 0)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => onDel(t.id)}><Trash2 className="w-4 h-4 text-[#C62828]" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="transfer-dialog">
          <DialogHeader><DialogTitle className="font-display">Nouveau transfert</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Type *</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="transfer-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="arrivee">Arrivée</SelectItem>
                  <SelectItem value="depart">Départ</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Joueur du club</Label>
              <Select value={form.player_id || "ext"} onValueChange={(v) => {
                if (v === "ext") setForm({ ...form, player_id: "" });
                else {
                  const p = players.find(p => p.id === v);
                  setForm({ ...form, player_id: v, player_name: `${p.first_name} ${p.last_name}` });
                }
              }}>
                <SelectTrigger data-testid="transfer-player"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ext">Joueur externe</SelectItem>
                  {players.map((p) => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nom du joueur *</Label>
              <Input required value={form.player_name} onChange={(e) => setForm({ ...form, player_name: e.target.value })} data-testid="transfer-player-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{form.type === "arrivee" ? "Club d'origine" : "Club de destination"}</Label>
                <Input value={form.type === "arrivee" ? form.from_club : form.to_club}
                  onChange={(e) => setForm({ ...form, [form.type === "arrivee" ? "from_club" : "to_club"]: e.target.value })} /></div>
              <div><Label>Date *</Label>
                <Input type="date" required value={form.transfer_date} onChange={(e) => setForm({ ...form, transfer_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Montant (FCFA)</Label><Input type="number" min={0} value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} data-testid="transfer-fee" /></div>
              <div><Label>Durée contrat</Label><Input placeholder="3 ans" value={form.contract_duration} onChange={(e) => setForm({ ...form, contract_duration: e.target.value })} /></div>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="btn-club" data-testid="transfer-submit-btn">Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="bg-white border border-[#E0E0E0] rounded-md p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs tracking-overline text-[#616161]">{label}</p>
        <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}15`, color }}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="font-display text-2xl font-extrabold tracking-tight" style={{ color }}>{value}</p>
    </div>
  );
}
